import { t } from 'i18next';
import { EMPTY, merge, of } from 'rxjs';
import { bufferTime, catchError, filter, ignoreElements, map, mergeMap, switchMap, takeUntil, tap } from 'rxjs/operators';
import { IEpic, isTypeOf, isTypeOfAndGetPayload } from '@cbridge-utils/redux-observable';
import { IClpApiDependencySegment, IClpError } from '@cbridge-packages/api-clp';
import { NotificationAction } from '@cbridge-packages/notification';
import { SymbolQuotesAction } from '../symbolQuotes.action';

// Batch ingested quote events into one store update per window to avoid React thrash on fast ticks.
const BATCH_INTERVAL_MS = 100;

const clpErrorKey = (error: IClpError): string => `${error.errorCode}|${error.errorMessage}`;

/**
 * Owns the CLP session lifecycle: opens the socket, funnels connection-state changes and price/error
 * events into store actions, and tears everything down on `disconnect`.
 */
export const connectEpic: IEpic<{}, IClpApiDependencySegment> = (action$, _, { clpApi }) =>
  action$.pipe(
    isTypeOfAndGetPayload(SymbolQuotesAction.connect),
    switchMap(({ clientId }) => {
      // CLP repeats the same ErrorRes on every (re)subscribe attempt (each 5 s reconnect while a
      // gateway is down) and never echoes request_id, so it can't be tied to a request. A mixed
      // viewport also yields an alternating A,B,A,B… stream, so consecutive-only dedup is not enough:
      // track every distinct error for this session (the set is recreated on each `connect`).
      const notifiedErrors = new Set<string>();

      return merge(
        clpApi.open$({ clientId }).pipe(
          switchMap(() => EMPTY),
          catchError((error) => of(SymbolQuotesAction.connectFailed({ error: String(error) }))),
        ),
        clpApi.connectionState$.pipe(map((state) => SymbolQuotesAction.connectionStateChanged({ state }))),
        clpApi.symbolPriceEvent$.pipe(
          bufferTime(BATCH_INTERVAL_MS),
          filter((events) => events.length > 0),
          map((events) => SymbolQuotesAction.ingestClientQuotes({ quotes: events.flatMap((event) => event.quotes) })),
        ),
        clpApi.lpPriceEvent$.pipe(
          bufferTime(BATCH_INTERVAL_MS),
          filter((events) => events.length > 0),
          map((events) => SymbolQuotesAction.ingestLpQuotes({ quotes: events.flatMap((event) => event.quotes) })),
        ),
        clpApi.error$.pipe(
          filter((apiError) => !notifiedErrors.has(clpErrorKey(apiError))),
          mergeMap((apiError) => {
            notifiedErrors.add(clpErrorKey(apiError));
            return [
              SymbolQuotesAction.failed({ error: apiError.errorMessage }),
              NotificationAction.show({
                message: t('text:dashboard.quotesView.clpError', { code: apiError.errorCode, message: apiError.errorMessage }),
                severity: 'error',
              }),
            ];
          }),
        ),
      ).pipe(takeUntil(action$.pipe(isTypeOf(SymbolQuotesAction.disconnect))));
    }),
  );

/** Closes the underlying WS on `disconnect` (the reducer already resets the quote store). */
export const disconnectEpic: IEpic<{}, IClpApiDependencySegment> = (action$, _, { clpApi }) =>
  action$.pipe(
    isTypeOf(SymbolQuotesAction.disconnect),
    tap(() => clpApi.close()),
    ignoreElements(),
  );
